import React from 'react';

import useRunOnce from '$/hooks/useRunOnce';
import { GdprContext } from '$/components/GdprCookie/GdprContext';
import { GDPR_COOKIE_CONSENT_OK_EVENT } from '$/widget/src/utils/gdprConsentEvent';
import { EmbeddedPlayerInfo, WidgetEventType } from '$/widget/src/interface';
import { UngatedSessionType } from '$/GraphQL/schema/types';
import { useHasChangedShallow } from '$/hooks/useHasChanged';

interface EmbeddedPlayerState {
  isExpanded: boolean;
  hadExpanded: boolean;
  isEmbeddedPlayer: boolean;

  playerInfo: EmbeddedPlayerInfo;

  toggleExpand: (expand: boolean) => void;

  onJoined: () => void;
  onReadyToJoin: (videoRatio: number) => void;
}

const useEmbeddedPlayer = (): EmbeddedPlayerState => {
  const cookiesAcceptedOutsideIframe = React.useRef(false);
  const { setIsAcceptedCookies, isAcceptedCookies } = React.useContext(GdprContext);
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [hadExpanded, setHadExpanded] = React.useState(false);
  const [playerInfo, setPlayerInfo] = React.useState<EmbeddedPlayerInfo>({
    width: window.innerWidth,
    height: window.innerHeight,
    sessionType: UngatedSessionType.OnDemand,
    thumbnail: '',
  });

  React.useEffect(() => {
    if (!isExpanded || hadExpanded) return;
    setHadExpanded(true);
  }, [isExpanded, hadExpanded]);

  const toggleExpand = React.useCallback(
    (nextState: boolean) => {
      if (nextState === isExpanded) return;
      window.xprops?.emit({
        event: WidgetEventType.PlayerToggleExpand,
        expand: nextState,
      });
    },
    [isExpanded]
  );

  const onReadyToJoin = React.useCallback((aspectRatio: number) => {
    window.xprops?.emit({
      event: WidgetEventType.PlayerReadyToJoin,
      aspectRatio,
    });
  }, []);

  const onJoined = React.useCallback(() => {
    window.xprops?.emit({
      event: WidgetEventType.PlayerJoined,
    });
  }, []);

  const isAcceptedCookiesChanged = useHasChangedShallow([isAcceptedCookies], { skipInitial: true });
  React.useEffect(() => {
    if (!isAcceptedCookies || !isAcceptedCookiesChanged) return;
    if (cookiesAcceptedOutsideIframe.current) return;
    window.xprops?.emit({
      event: WidgetEventType.IFrameAcceptedCookies,
    });
  }, [isAcceptedCookies, isAcceptedCookiesChanged]);

  useRunOnce(() => {
    if (!window.eWebinarWidget) {
      const { createWidget } = require('$/widget/src/widget');
      window.eWebinarWidget = createWidget();

      window.xprops?.on((e) => {
        switch (e.event) {
          case WidgetEventType.PlayerUpdateInfo:
            setPlayerInfo(e.data);
            break;
          case WidgetEventType.PlayerToggleExpand:
            setIsExpanded(e.expand);
            break;
          case WidgetEventType.AcceptedCookies: {
            setIsAcceptedCookies(true);
            cookiesAcceptedOutsideIframe.current = true;
            const cookiesAcceptedEvent = new Event(GDPR_COOKIE_CONSENT_OK_EVENT);
            window.dispatchEvent(cookiesAcceptedEvent);
            break;
          }
        }
      });

      window.xprops?.emit({
        event: WidgetEventType.IFrameReady,
      });
    }
  });

  return {
    isExpanded,
    hadExpanded,
    isEmbeddedPlayer: true,
    playerInfo,

    toggleExpand,

    onJoined,
    onReadyToJoin,
  };
};

const EmbeddedPlayerContext = React.createContext({
  isExpanded: false,
  isEmbeddedPlayer: false,
} as EmbeddedPlayerState);

export const EmbeddedPlayerProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const value = useEmbeddedPlayer();
  return <EmbeddedPlayerContext.Provider value={value}>{children}</EmbeddedPlayerContext.Provider>;
};

export const useEmbeddedPlayerContext = (): EmbeddedPlayerState =>
  React.useContext(EmbeddedPlayerContext);
